import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Package, 
  Database, 
  Settings, 
  FileSpreadsheet, 
  BookOpen,
  CheckCircle2,
  Info,
  ArrowRight,
  Boxes,
  Calculator,
  Bot,
  Copy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const ProductDocumentation = () => {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-4 py-8">
          <div className="flex items-center justify-center gap-3">
            <Package className="w-12 h-12 text-[#1B4332]" />
            <h1 className="text-4xl font-bold text-gray-900">Brique Produit OrderLab</h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Documentation complète du système de gestion des produits
          </p>
          <div className="flex items-center justify-center gap-2">
            <Badge className="bg-[#1B4332]">Version 1.0</Badge>
            <Badge variant="outline">14 avril 2026</Badge>
          </div>
        </div>

        {/* Navigation Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6 h-auto">
            <TabsTrigger value="overview" className="flex items-center gap-2 py-3">
              <BookOpen className="w-4 h-4" />
              Vue d'ensemble
            </TabsTrigger>
            <TabsTrigger value="fields" className="flex items-center gap-2 py-3">
              <Database className="w-4 h-4" />
              Champs
            </TabsTrigger>
            <TabsTrigger value="packaging" className="flex items-center gap-2 py-3">
              <Boxes className="w-4 h-4" />
              Conditionnement
            </TabsTrigger>
            <TabsTrigger value="import" className="flex items-center gap-2 py-3">
              <FileSpreadsheet className="w-4 h-4" />
              Import
            </TabsTrigger>
            <TabsTrigger value="examples" className="flex items-center gap-2 py-3">
              <Settings className="w-4 h-4" />
              Exemples
            </TabsTrigger>
            <TabsTrigger value="llm" className="flex items-center gap-2 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-pink-600">
              <Bot className="w-4 h-4" />
              Full LLM
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-[#1B4332]" />
                  Qu'est-ce qu'un produit dans OrderLab ?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  Un <strong>produit</strong> dans OrderLab représente un article qu'un restaurant peut commander auprès d'un fournisseur. 
                  Chaque produit est unique et appartient à <strong>UN SEUL fournisseur</strong>.
                </p>
                
                <Alert className="bg-blue-50 border-blue-200">
                  <Info className="w-4 h-4 text-blue-600" />
                  <AlertDescription className="text-blue-900">
                    <strong>Philosophie du système :</strong> OrderLab gère DEUX SYSTÈMES en parallèle pour garantir 
                    la compatibilité ascendante tout en offrant des fonctionnalités avancées.
                  </AlertDescription>
                </Alert>

                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  <Card className="border-2 border-green-200 bg-green-50">
                    <CardHeader>
                      <CardTitle className="text-lg text-green-900">🆕 Nouveau Système</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-green-800">
                        Système de conditionnement avancé avec détaillabilité. 
                        Permet de modéliser précisément comment le fournisseur vend et comment le restaurant peut commander.
                      </p>
                      <Badge className="mt-3 bg-green-600">Recommandé</Badge>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-amber-200 bg-amber-50">
                    <CardHeader>
                      <CardTitle className="text-lg text-amber-900">🔄 Ancien Système</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-amber-800">
                        Système simplifié pour les produits existants. 
                        Supporté pour garantir la compatibilité avec les données historiques.
                      </p>
                      <Badge className="mt-3 bg-amber-600">Compatibilité</Badge>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#1B4332]" />
                  Champs Obligatoires (Minimum Viable)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-3 text-left border">Champ</th>
                        <th className="p-3 text-left border">Type</th>
                        <th className="p-3 text-left border">Description</th>
                        <th className="p-3 text-left border">Exemple</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="p-3 border font-mono text-purple-600">code</td>
                        <td className="p-3 border">String</td>
                        <td className="p-3 border">Code de référence unique du produit</td>
                        <td className="p-3 border font-mono text-sm">"REF123"</td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="p-3 border font-mono text-purple-600">article</td>
                        <td className="p-3 border">String</td>
                        <td className="p-3 border">Nom complet du produit</td>
                        <td className="p-3 border">"Tomates cerises bio"</td>
                      </tr>
                      <tr>
                        <td className="p-3 border font-mono text-purple-600">supplier_id</td>
                        <td className="p-3 border">UUID</td>
                        <td className="p-3 border">ID du fournisseur propriétaire</td>
                        <td className="p-3 border font-mono text-xs">"e68c101a-..."</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <Alert className="mt-4 bg-red-50 border-red-200">
                  <AlertDescription className="text-red-900">
                    ⚠️ <strong>Important :</strong> Ces 3 champs sont le minimum absolu pour créer un produit.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Fields Tab */}
          <TabsContent value="fields" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-[#1B4332]" />
                  Modèle de Données Complet
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Nouveau Système */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-green-900 flex items-center gap-2">
                    <Boxes className="w-5 h-5" />
                    🆕 Nouveau Système : Conditionnement & Détaillabilité
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-green-50">
                        <tr>
                          <th className="p-3 text-left border">Champ</th>
                          <th className="p-3 text-left border">Type</th>
                          <th className="p-3 text-left border">Description</th>
                          <th className="p-3 text-left border">Exemple</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="p-3 border font-mono text-xs">supplier_packaging_quantity</td>
                          <td className="p-3 border">Float</td>
                          <td className="p-3 border">Quantité dans un conditionnement</td>
                          <td className="p-3 border">6.0</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="p-3 border font-mono text-xs">supplier_packaging_unit</td>
                          <td className="p-3 border">String</td>
                          <td className="p-3 border">Unité de la quantité</td>
                          <td className="p-3 border">"L", "KG"</td>
                        </tr>
                        <tr>
                          <td className="p-3 border font-mono text-xs">supplier_packaging_type</td>
                          <td className="p-3 border">String</td>
                          <td className="p-3 border">Type de conditionnement</td>
                          <td className="p-3 border">"colis", "carton"</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="p-3 border font-mono text-xs">supplier_package_price</td>
                          <td className="p-3 border">Float</td>
                          <td className="p-3 border">Prix HT du conditionnement complet</td>
                          <td className="p-3 border">42.50€</td>
                        </tr>
                        <tr>
                          <td className="p-3 border font-mono text-xs">is_divisible</td>
                          <td className="p-3 border">Boolean</td>
                          <td className="p-3 border">Le produit est-il détaillable ?</td>
                          <td className="p-3 border">true</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="p-3 border font-mono text-xs">divisible_quantity</td>
                          <td className="p-3 border">Float</td>
                          <td className="p-3 border">Quantité minimale commandable</td>
                          <td className="p-3 border">1.0</td>
                        </tr>
                        <tr>
                          <td className="p-3 border font-mono text-xs">divisible_unit</td>
                          <td className="p-3 border">String</td>
                          <td className="p-3 border">Unité de la quantité détaillable</td>
                          <td className="p-3 border">"L", "KG"</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Prix Catalogue */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-blue-900 flex items-center gap-2">
                    <Calculator className="w-5 h-5" />
                    Prix Catalogue (Auto-calculé)
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-blue-50">
                        <tr>
                          <th className="p-3 text-left border">Champ</th>
                          <th className="p-3 text-left border">Type</th>
                          <th className="p-3 text-left border">Description</th>
                          <th className="p-3 text-left border">Calcul</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="p-3 border font-mono text-xs">catalog_price</td>
                          <td className="p-3 border">Float</td>
                          <td className="p-3 border">Prix unitaire affiché au catalogue</td>
                          <td className="p-3 border font-mono text-xs">Auto ou manuel</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="p-3 border font-mono text-xs">catalog_unit</td>
                          <td className="p-3 border">String</td>
                          <td className="p-3 border">Unité du prix catalogue</td>
                          <td className="p-3 border">"L", "colis", "KG"</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <Alert className="mt-4 bg-blue-50 border-blue-200">
                    <Info className="w-4 h-4 text-blue-600" />
                    <AlertDescription className="text-blue-900">
                      <strong>Logique de calcul automatique :</strong><br/>
                      • Si détaillable : catalog_price = supplier_package_price ÷ supplier_packaging_quantity<br/>
                      • Si non détaillable : catalog_price = supplier_package_price
                    </AlertDescription>
                  </Alert>
                </div>

                {/* Ancien Système */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-amber-900">
                    🔄 Ancien Système : Compatibilité
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-amber-50">
                        <tr>
                          <th className="p-3 text-left border">Champ</th>
                          <th className="p-3 text-left border">Type</th>
                          <th className="p-3 text-left border">Description</th>
                          <th className="p-3 text-left border">Exemple</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="p-3 border font-mono text-xs">min_packaging</td>
                          <td className="p-3 border">String</td>
                          <td className="p-3 border">Conditionnement minimum (texte libre)</td>
                          <td className="p-3 border">"2kg", "Colis 6x1L"</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="p-3 border font-mono text-xs">unit</td>
                          <td className="p-3 border">String</td>
                          <td className="p-3 border">Unité de commande</td>
                          <td className="p-3 border">"KG", "PIECE"</td>
                        </tr>
                        <tr>
                          <td className="p-3 border font-mono text-xs">unit_price</td>
                          <td className="p-3 border">Float</td>
                          <td className="p-3 border">Prix unitaire simple</td>
                          <td className="p-3 border">15.50€</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Métadonnées */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-900">
                    Métadonnées & Classification
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="p-3 text-left border">Champ</th>
                          <th className="p-3 text-left border">Type</th>
                          <th className="p-3 text-left border">Description</th>
                          <th className="p-3 text-left border">Valeurs possibles</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="p-3 border font-mono text-xs">product_type</td>
                          <td className="p-3 border">String</td>
                          <td className="p-3 border">Type de produit</td>
                          <td className="p-3 border text-xs">frais, surgeles, epicerie-seche...</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="p-3 border font-mono text-xs">rayon</td>
                          <td className="p-3 border">String</td>
                          <td className="p-3 border">Rayon du produit</td>
                          <td className="p-3 border text-xs">viandes-volailles, poissons...</td>
                        </tr>
                        <tr>
                          <td className="p-3 border font-mono text-xs">is_available</td>
                          <td className="p-3 border">Boolean</td>
                          <td className="p-3 border">Disponible à la commande ?</td>
                          <td className="p-3 border">true / false</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Packaging Tab */}
          <TabsContent value="packaging" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Boxes className="w-5 h-5 text-[#1B4332]" />
                  Système de Conditionnement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <Alert className="bg-purple-50 border-purple-200">
                  <Info className="w-4 h-4 text-purple-600" />
                  <AlertDescription className="text-purple-900">
                    <strong>Pourquoi ce système ?</strong> Le nouveau système permet de gérer avec précision 
                    comment le fournisseur vend (conditionnement) et comment le restaurant peut commander (détaillabilité).
                  </AlertDescription>
                </Alert>

                {/* Cas 1 */}
                <Card className="border-2 border-green-200">
                  <CardHeader className="bg-green-50">
                    <CardTitle className="text-lg text-green-900">
                      📦 Cas 1 : Huile d'olive en colis (6 × 1L)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Non détaillable */}
                      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                        <h4 className="font-semibold text-red-900 mb-2">❌ NON Détaillable</h4>
                        <div className="text-sm text-gray-700 space-y-1">
                          <p><strong>Configuration :</strong></p>
                          <pre className="bg-white p-2 rounded text-xs overflow-x-auto">
{`{
  "supplier_packaging_quantity": 6,
  "supplier_packaging_unit": "L",
  "supplier_packaging_type": "colis",
  "supplier_package_price": 48.00,
  "is_divisible": false
}`}
                          </pre>
                          <p className="mt-2"><strong>Résultat :</strong></p>
                          <p className="text-red-800">
                            <ArrowRight className="w-4 h-4 inline mr-1" />
                            Prix affiché : <strong>48,00€ / colis</strong>
                          </p>
                          <p className="text-red-800">
                            <ArrowRight className="w-4 h-4 inline mr-1" />
                            Commande : par colis entier uniquement
                          </p>
                        </div>
                      </div>

                      {/* Détaillable */}
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <h4 className="font-semibold text-green-900 mb-2">✅ Détaillable</h4>
                        <div className="text-sm text-gray-700 space-y-1">
                          <p><strong>Configuration :</strong></p>
                          <pre className="bg-white p-2 rounded text-xs overflow-x-auto">
{`{
  "supplier_packaging_quantity": 6,
  "supplier_packaging_unit": "L",
  "supplier_packaging_type": "colis",
  "supplier_package_price": 48.00,
  "is_divisible": true,
  "divisible_quantity": 1,
  "divisible_unit": "L"
}`}
                          </pre>
                          <p className="mt-2"><strong>Résultat :</strong></p>
                          <p className="text-green-800">
                            <ArrowRight className="w-4 h-4 inline mr-1" />
                            Prix affiché : <strong>8,00€ / L</strong> (48 ÷ 6)
                          </p>
                          <p className="text-green-800">
                            <ArrowRight className="w-4 h-4 inline mr-1" />
                            Commande : 1L, 2L, 3L, etc.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Cas 2 */}
                <Card className="border-2 border-blue-200">
                  <CardHeader className="bg-blue-50">
                    <CardTitle className="text-lg text-blue-900">
                      🥩 Cas 2 : Viande hachée en carton (10 KG)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-900 mb-2">✅ Détaillable par 500g</h4>
                      <div className="text-sm text-gray-700 space-y-1">
                        <p><strong>Configuration :</strong></p>
                        <pre className="bg-white p-2 rounded text-xs overflow-x-auto">
{`{
  "supplier_packaging_quantity": 10,
  "supplier_packaging_unit": "KG",
  "supplier_packaging_type": "carton",
  "supplier_package_price": 85.00,
  "is_divisible": true,
  "divisible_quantity": 0.5,
  "divisible_unit": "KG"
}`}
                        </pre>
                        <p className="mt-2"><strong>Résultat :</strong></p>
                        <p className="text-blue-800">
                          <ArrowRight className="w-4 h-4 inline mr-1" />
                          Prix affiché : <strong>4,25€ / 500g</strong> (85 ÷ 10 × 0.5)
                        </p>
                        <p className="text-blue-800">
                          <ArrowRight className="w-4 h-4 inline mr-1" />
                          Commande : par multiples de 500g
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Import Tab */}
          <TabsContent value="import" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-[#1B4332]" />
                  Import de Produits (CSV/Excel)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Format CSV/Excel Attendu</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-sm mb-2">📊 Colonnes Minimum</h4>
                      <pre className="bg-gray-900 text-green-400 p-4 rounded text-xs overflow-x-auto">
{`Code,Article
REF001,Tomates cerises bio
REF002,Huile d'olive`}
                      </pre>
                    </div>

                    <div>
                      <h4 className="font-medium text-sm mb-2">📊 Colonnes Complètes (Nouveau Système)</h4>
                      <pre className="bg-gray-900 text-green-400 p-4 rounded text-xs overflow-x-auto">
{`Code,Article,Type,Rayon,Qté conditionnement,Unité conditionnement,Type conditionnement,Prix conditionnement (€),Détaillable (oui/non),Qté divisible,Unité divisible
REF001,Tomates cerises bio,frais,fruits-legumes-frais,5,KG,carton,12.50,oui,1,KG
REF002,Huile d'olive,epicerie-salee,huiles-vinaigres,6,L,colis,48.00,oui,1,L`}
                      </pre>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Processus d'Import</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#1B4332] text-white flex items-center justify-center flex-shrink-0 font-bold">
                        1
                      </div>
                      <div>
                        <h4 className="font-medium">Upload fichier</h4>
                        <p className="text-sm text-gray-600">Formats acceptés : .xlsx, .xls, .csv</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#1B4332] text-white flex items-center justify-center flex-shrink-0 font-bold">
                        2
                      </div>
                      <div>
                        <h4 className="font-medium">Parsing automatique</h4>
                        <p className="text-sm text-gray-600">Détection des en-têtes et analyse du contenu</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#1B4332] text-white flex items-center justify-center flex-shrink-0 font-bold">
                        3
                      </div>
                      <div>
                        <h4 className="font-medium">Mapping colonnes</h4>
                        <p className="text-sm text-gray-600">Association automatique + ajustement manuel possible</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#1B4332] text-white flex items-center justify-center flex-shrink-0 font-bold">
                        4
                      </div>
                      <div>
                        <h4 className="font-medium">Prévisualisation éditable</h4>
                        <p className="text-sm text-gray-600">Tableau où chaque ligne peut être modifiée avant import</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#1B4332] text-white flex items-center justify-center flex-shrink-0 font-bold">
                        5
                      </div>
                      <div>
                        <h4 className="font-medium">Import final</h4>
                        <p className="text-sm text-gray-600">Création batch avec gestion des doublons</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-medium">Résultat</h4>
                        <p className="text-sm text-gray-600">Rapport détaillé : créés/mis à jour/erreurs</p>
                      </div>
                    </div>
                  </div>
                </div>

                <Alert className="bg-amber-50 border-amber-200">
                  <Info className="w-4 h-4 text-amber-600" />
                  <AlertDescription className="text-amber-900">
                    <strong>Gestion des doublons :</strong><br/>
                    • update_existing = false : Produit ignoré (skipped)<br/>
                    • update_existing = true : Produit mis à jour
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Examples Tab */}
          <TabsContent value="examples" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[#1B4332]" />
                  Cas d'Usage Concrets
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Cas 1 */}
                <Card className="border-2 border-purple-200">
                  <CardHeader className="bg-purple-50">
                    <CardTitle className="text-lg text-purple-900">
                      🍖 Cas 1 : Restaurant qui commande au kg
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-3">
                    <p className="text-sm text-gray-700">
                      <strong>Contexte :</strong> Viande hachée vendue en cartons de 10kg par le fournisseur.
                    </p>
                    <pre className="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto">
{`{
  "supplier_packaging_quantity": 10,
  "supplier_packaging_unit": "KG",
  "supplier_packaging_type": "carton",
  "supplier_package_price": 85.00,
  "is_divisible": true,
  "divisible_quantity": 1,
  "divisible_unit": "KG"
}`}
                    </pre>
                    <div className="bg-purple-50 p-3 rounded">
                      <p className="text-sm font-medium text-purple-900">Comportement :</p>
                      <ul className="text-sm text-purple-800 space-y-1 mt-2">
                        <li>• Catalogue affiche : <strong>8,50€ / KG</strong></li>
                        <li>• Le restaurant commande : 15 KG</li>
                        <li>• Système calcule : 15 × 8.50 = <strong>127,50€</strong></li>
                        <li>• Fournisseur reçoit : 1,5 cartons</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                {/* Cas 2 */}
                <Card className="border-2 border-blue-200">
                  <CardHeader className="bg-blue-50">
                    <CardTitle className="text-lg text-blue-900">
                      🍕 Cas 2 : Restaurant qui commande par unités entières
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-3">
                    <p className="text-sm text-gray-700">
                      <strong>Contexte :</strong> Pizza surgelée vendue en cartons de 24 pièces.
                    </p>
                    <pre className="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto">
{`{
  "supplier_packaging_quantity": 24,
  "supplier_packaging_unit": "U",
  "supplier_packaging_type": "carton",
  "supplier_package_price": 96.00,
  "is_divisible": false
}`}
                    </pre>
                    <div className="bg-blue-50 p-3 rounded">
                      <p className="text-sm font-medium text-blue-900">Comportement :</p>
                      <ul className="text-sm text-blue-800 space-y-1 mt-2">
                        <li>• Catalogue affiche : <strong>96,00€ / carton</strong></li>
                        <li>• Le restaurant doit commander : 1, 2, 3... cartons (pas de demi-carton)</li>
                        <li>• Système valide : Quantité = multiple du conditionnement</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                {/* Cas 3 */}
                <Card className="border-2 border-green-200">
                  <CardHeader className="bg-green-50">
                    <CardTitle className="text-lg text-green-900">
                      📄 Cas 3 : Import depuis un tarif fournisseur
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-3">
                    <p className="text-sm text-gray-700">
                      <strong>Scénario :</strong> Le fournisseur envoie un fichier Excel avec ses tarifs.
                    </p>
                    <div className="bg-white border rounded p-3">
                      <p className="text-xs font-mono mb-2">Fichier Excel du fournisseur :</p>
                      <table className="w-full text-xs">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="p-2 text-left border">Référence</th>
                            <th className="p-2 text-left border">Désignation</th>
                            <th className="p-2 text-left border">Cond.</th>
                            <th className="p-2 text-right border">Prix</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="p-2 border">TOM-001</td>
                            <td className="p-2 border">Tomates cerises</td>
                            <td className="p-2 border">5kg</td>
                            <td className="p-2 text-right border">12.50€</td>
                          </tr>
                          <tr>
                            <td className="p-2 border">HUI-002</td>
                            <td className="p-2 border">Huile d'olive EV</td>
                            <td className="p-2 border">6x1L</td>
                            <td className="p-2 text-right border">48.00€</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="bg-green-50 p-3 rounded">
                      <p className="text-sm font-medium text-green-900">Import dans OrderLab :</p>
                      <ol className="text-sm text-green-800 space-y-1 mt-2 list-decimal list-inside">
                        <li>Mapper les colonnes</li>
                        <li>Enrichir manuellement : définir détaillabilité</li>
                        <li>Importer → 3 produits créés automatiquement</li>
                      </ol>
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>

            {/* Summary Card */}
            <Card className="bg-gradient-to-br from-[#D8F3DC] to-[#F1F8F4] border-2 border-[#1B4332]">
              <CardHeader>
                <CardTitle className="text-[#1B4332]">🎯 Points Clés à Retenir</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-800">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#1B4332] flex-shrink-0 mt-0.5" />
                    <span><strong>Deux systèmes coexistent :</strong> Nouveau (conditionnement) et Ancien (compatibilité)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#1B4332] flex-shrink-0 mt-0.5" />
                    <span><strong>Champs minimum :</strong> Code + Article + Fournisseur</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#1B4332] flex-shrink-0 mt-0.5" />
                    <span><strong>Nouveau système :</strong> Modélise précisément conditionnement et détaillabilité</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#1B4332] flex-shrink-0 mt-0.5" />
                    <span><strong>Prix catalogue :</strong> Calculé automatiquement si nouveau système utilisé</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#1B4332] flex-shrink-0 mt-0.5" />
                    <span><strong>Import flexible :</strong> CSV/Excel avec mapping + prévisualisation éditable</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#1B4332] flex-shrink-0 mt-0.5" />
                    <span><strong>APIs complètes :</strong> CRUD + Import batch + Filtres</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Full LLM Tab */}
          <TabsContent value="llm" className="space-y-6 mt-6">
            <Card className="border-2 border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-900">
                  <Bot className="w-6 h-6" />
                  Documentation Complète pour IA (Format LLM)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-purple-100 border-purple-300">
                  <Info className="w-4 h-4 text-purple-700" />
                  <AlertDescription className="text-purple-900">
                    <strong>Format optimisé pour les LLMs :</strong> Copiez tout le texte ci-dessous et collez-le dans votre IA 
                    (ChatGPT, Claude, Gemini, etc.) pour lui donner le contexte complet de la brique produit OrderLab.
                  </AlertDescription>
                </Alert>

                <div className="flex justify-end">
                  <Button 
                    onClick={() => {
                      const text = document.getElementById('llm-content').innerText;
                      navigator.clipboard.writeText(text);
                      toast.success('Documentation copiée dans le presse-papiers ! 📋');
                    }}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copier tout le texte
                  </Button>
                </div>

                <div 
                  id="llm-content"
                  className="bg-gray-900 text-green-400 p-6 rounded-lg text-sm font-mono overflow-x-auto max-h-[600px] overflow-y-auto leading-relaxed whitespace-pre-wrap"
                  style={{ fontFamily: 'Monaco, Consolas, monospace' }}
                >
{`# DOCUMENTATION COMPLÈTE - BRIQUE PRODUIT ORDERLAB

## 📋 CONTEXTE GÉNÉRAL

OrderLab est un système de gestion de commandes pour restaurants qui permet de :
- Gérer un catalogue de produits
- Créer des commandes auprès de fournisseurs
- Suivre les livraisons et réceptions
- Gérer plusieurs utilisateurs et équipes

Cette documentation se concentre sur la **brique produit**, c'est-à-dire le système de gestion du catalogue de produits.

---

## 🎯 DÉFINITION D'UN PRODUIT

Un **produit** dans OrderLab représente un article qu'un restaurant peut commander auprès d'un fournisseur.

**Règles fondamentales :**
- Chaque produit appartient à UN SEUL fournisseur (relation 1:N)
- Un produit a un code unique (référence fournisseur)
- Un produit peut être disponible ou indisponible
- Un produit peut être classé par type et rayon

---

## 🔄 DOUBLE SYSTÈME

OrderLab gère DEUX SYSTÈMES en parallèle :

### 1. NOUVEAU SYSTÈME (Recommandé)
- Système de conditionnement avancé avec détaillabilité
- Permet de modéliser précisément comment le fournisseur vend le produit
- Permet de définir comment le restaurant peut commander (par unité, par kg, etc.)
- Calcul automatique du prix catalogue

### 2. ANCIEN SYSTÈME (Compatibilité)
- Système simplifié avec champs texte libres
- Supporté pour garantir la compatibilité avec les produits existants
- Moins précis mais plus simple

---

## 📊 MODÈLE DE DONNÉES COMPLET

### CHAMPS OBLIGATOIRES (Minimum viable)

\`\`\`
code (String)
  - Code de référence unique du produit chez le fournisseur
  - Exemples : "REF123", "TOMATE-BIO-001", "HUI-OLIVE-EV"
  - Obligatoire : OUI

article (String)
  - Nom complet du produit
  - Exemples : "Tomates cerises bio", "Huile d'olive extra vierge"
  - Obligatoire : OUI

supplier_id (UUID)
  - ID du fournisseur propriétaire
  - Format : UUID v4
  - Exemple : "e68c101a-3367-4dcd-90da-e42675553f8f"
  - Obligatoire : OUI
\`\`\`

⚠️ **IMPORTANT** : Ces 3 champs sont le minimum absolu pour créer un produit.

---

### NOUVEAU SYSTÈME : Conditionnement fournisseur

Ces champs décrivent comment le fournisseur vend le produit.

\`\`\`
supplier_packaging_quantity (Float, nullable)
  - Quantité dans un conditionnement
  - Exemple : 6.0 pour "6 × 1L"
  - Défaut : null

supplier_packaging_unit (String, nullable)
  - Unité de la quantité
  - Valeurs possibles : "L", "KG", "G", "ML", "U" (unité/pièce)
  - Exemple : "L" pour litres
  - Défaut : "KG"

supplier_packaging_type (String, nullable)
  - Type de conditionnement
  - Valeurs possibles : "colis", "carton", "pièce", "sac"
  - Exemple : "colis"
  - Défaut : "colis"

supplier_package_price (Float, nullable)
  - Prix HT du conditionnement complet
  - Exemple : 42.50 (pour un colis de 6L à 42,50€)
  - Défaut : null
\`\`\`

**Exemple concret :**
\`\`\`json
{
  "supplier_packaging_quantity": 6,
  "supplier_packaging_unit": "L",
  "supplier_packaging_type": "colis",
  "supplier_package_price": 42.50
}
\`\`\`
📌 Signification : Le fournisseur vend ce produit en colis de 6 litres au prix de 42,50€ HT le colis.

---

### NOUVEAU SYSTÈME : Détaillabilité

Ces champs indiquent si le produit peut être commandé en quantité inférieure au conditionnement fournisseur.

\`\`\`
is_divisible (Boolean)
  - Le produit est-il détaillable ?
  - true = on peut commander moins qu'un conditionnement complet
  - false = on doit commander des conditionnements entiers
  - Exemple : true
  - Défaut : false

divisible_quantity (Float, nullable)
  - Quantité minimale commandable si détaillable
  - Exemple : 1.0 pour "commandable par 1L"
  - Défaut : 1.0

divisible_unit (String, nullable)
  - Unité de la quantité détaillable
  - Valeurs possibles : "L", "KG", "G", "ML", "U"
  - Exemple : "L"
  - Défaut : "KG"
\`\`\`

**Exemple 1 - Produit DÉTAILLABLE :**
\`\`\`json
{
  "is_divisible": true,
  "divisible_quantity": 1,
  "divisible_unit": "L"
}
\`\`\`
📌 Signification : Le restaurant peut commander par litre (même si le fournisseur vend en colis de 6L).

**Exemple 2 - Produit NON DÉTAILLABLE :**
\`\`\`json
{
  "is_divisible": false
}
\`\`\`
📌 Signification : Le restaurant doit commander un conditionnement entier (impossible de commander 3L, uniquement des colis complets).

---

### NOUVEAU SYSTÈME : Prix catalogue (Auto-calculé)

\`\`\`
catalog_price (Float, nullable)
  - Prix unitaire affiché au catalogue
  - Calculé automatiquement ou modifiable manuellement
  - Exemple : 7.08
  - Défaut : null

catalog_unit (String, nullable)
  - Unité du prix catalogue
  - Exemples : "L", "colis", "KG", "carton"
  - Auto-déterminé ou manuel
  - Défaut : null
\`\`\`

**LOGIQUE DE CALCUL AUTOMATIQUE :**

\`\`\`javascript
// Si le produit est DÉTAILLABLE
if (is_divisible && supplier_packaging_quantity > 0 && supplier_package_price > 0) {
  catalog_price = supplier_package_price / supplier_packaging_quantity;
  catalog_unit = divisible_unit;
}

// Si le produit est NON DÉTAILLABLE
else if (!is_divisible && supplier_package_price > 0) {
  catalog_price = supplier_package_price;
  catalog_unit = supplier_packaging_type;
}
\`\`\`

**Exemple complet :**
\`\`\`json
{
  "supplier_packaging_quantity": 6,
  "supplier_packaging_unit": "L",
  "supplier_packaging_type": "colis",
  "supplier_package_price": 42.50,
  "is_divisible": true,
  "divisible_quantity": 1,
  "divisible_unit": "L",
  "catalog_price": 7.08,  // 42.50 ÷ 6 = 7.08
  "catalog_unit": "L"
}
\`\`\`

---

### ANCIEN SYSTÈME : Compatibilité

Pour les produits créés avant le nouveau système ou pour une saisie simplifiée.

\`\`\`
min_packaging (String, nullable)
  - Conditionnement minimum (texte libre)
  - Exemples : "2kg", "Colis 6x1L", "Sac 25kg"
  - Défaut : ""

unit (String, nullable)
  - Unité de commande (texte libre)
  - Exemples : "KG", "PIECE", "COLIS", "L"
  - Défaut : ""

order_unit (String, nullable)
  - Description du conditionnement
  - Exemples : "Carton de 24", "Sac 25kg", "Bouteille 1L"
  - Défaut : ""

min_quantity (String, nullable)
  - Quantité minimale à commander
  - Exemples : "1", "5", "10"
  - Défaut : "1"

price_description (String, nullable)
  - Description du prix (optionnel)
  - Exemples : "15€/carton de 24", "8,50€/kg"
  - Défaut : ""

unit_price (Float, nullable)
  - Prix unitaire simple
  - Exemple : 15.50
  - Défaut : null
\`\`\`

⚠️ **Important** : L'ancien système est moins précis mais reste supporté pour garantir la compatibilité avec les données existantes.

---

### MÉTADONNÉES & CLASSIFICATION

\`\`\`
product_type (String, nullable)
  - Type de produit
  - Valeurs possibles :
    • "frais" : Viandes, poissons, produits laitiers, fruits et légumes frais
    • "surgeles" : Tous les produits surgelés
    • "epicerie-seche" : Pâtes, riz, farine, conserves
    • "condiments-sauces" : Sauces, huiles, vinaigres, épices
    • "boissons-alcoolisees" : Vins, bières, spiritueux
    • "boissons-non-alcoolisees" : Jus, sodas, eaux, café, thé
    • "boulangerie-patisserie" : Pain, viennoiseries, pâtisseries
    • "non-alimentaire" : Hygiène et produits d'entretien
  - Défaut : ""

rayon (String, nullable)
  - Rayon du produit
  - Valeurs possibles (liste non exhaustive) :
    • "viandes-volailles"
    • "poissons-fruits-mer"
    • "produits-laitiers-oeufs"
    • "fruits-legumes-frais"
    • "surgeles"
    • "epicerie-salee"
    • "epicerie-sucree"
    • "conserves"
    • "huiles-vinaigres"
    • "sauces-condiments"
    • "boissons-alcoolisees"
    • "boissons-non-alcoolisees"
    • "pain-viennoiseries"
    • "patisserie"
    • "epices-aromates"
    • "hygiene-alimentaire"
    • "produits-entretien"
  - Défaut : ""

is_available (Boolean)
  - Produit disponible à la commande ?
  - true = disponible (affiché dans le catalogue)
  - false = indisponible (masqué ou désactivé)
  - Défaut : true

created_at (ISO DateTime)
  - Date de création du produit
  - Format : ISO 8601 avec timezone UTC
  - Exemple : "2026-04-14T10:20:31Z"
  - Généré automatiquement
\`\`\`

---

### INFORMATIONS FOURNISSEUR (Lecture seule)

Ces champs sont enrichis automatiquement depuis le fournisseur associé lors de la lecture.

\`\`\`
supplier_name (String)
  - Nom du fournisseur
  - Exemple : "Passion Froid"
  - Lecture seule (jointure)

supplier_delivery_days (Array[Integer])
  - Jours de livraison du fournisseur
  - Format : 0=Lundi, 1=Mardi, ..., 6=Dimanche
  - Exemple : [1, 3, 5] = Mardi, Jeudi, Samedi
  - Lecture seule (jointure)

supplier_lead_time_days (Integer)
  - Délai de livraison en jours
  - Exemple : 2 = livraison J+2
  - Lecture seule (jointure)

supplier_cutoff_hour (Integer)
  - Heure limite de commande (format 24h)
  - Exemple : 12 = commande avant 12h
  - Lecture seule (jointure)
\`\`\`

---

## 💡 CAS D'USAGE CONCRETS

### CAS 1 : Huile d'olive en colis (6 × 1L)

**Contexte :** Un fournisseur vend de l'huile d'olive en colis de 6 bouteilles de 1L.

#### Option A : Produit NON DÉTAILLABLE

\`\`\`json
{
  "code": "HUILE-OLIVE-001",
  "article": "Huile d'olive extra vierge",
  "supplier_id": "uuid-fournisseur",
  "supplier_packaging_quantity": 6,
  "supplier_packaging_unit": "L",
  "supplier_packaging_type": "colis",
  "supplier_package_price": 48.00,
  "is_divisible": false,
  "catalog_price": 48.00,
  "catalog_unit": "colis"
}
\`\`\`

**Résultat :**
- Catalogue affiche : 48,00€ / colis
- Le restaurant commande par colis entier uniquement
- Impossible de commander 3L, seulement 1 colis, 2 colis, etc.

#### Option B : Produit DÉTAILLABLE

\`\`\`json
{
  "code": "HUILE-OLIVE-001",
  "article": "Huile d'olive extra vierge",
  "supplier_id": "uuid-fournisseur",
  "supplier_packaging_quantity": 6,
  "supplier_packaging_unit": "L",
  "supplier_packaging_type": "colis",
  "supplier_package_price": 48.00,
  "is_divisible": true,
  "divisible_quantity": 1,
  "divisible_unit": "L",
  "catalog_price": 8.00,  // 48 ÷ 6
  "catalog_unit": "L"
}
\`\`\`

**Résultat :**
- Catalogue affiche : 8,00€ / L
- Le restaurant peut commander 1L, 2L, 3L, 5L, etc.
- Flexibilité maximale

---

### CAS 2 : Viande hachée en carton (10 KG)

**Contexte :** Un fournisseur vend de la viande hachée en cartons de 10 kg.

#### Produit DÉTAILLABLE par 500g

\`\`\`json
{
  "code": "VIANDE-HACHEE-001",
  "article": "Viande hachée 15% MG",
  "supplier_id": "uuid-fournisseur",
  "supplier_packaging_quantity": 10,
  "supplier_packaging_unit": "KG",
  "supplier_packaging_type": "carton",
  "supplier_package_price": 85.00,
  "is_divisible": true,
  "divisible_quantity": 0.5,
  "divisible_unit": "KG",
  "catalog_price": 4.25,  // (85 ÷ 10) × 0.5
  "catalog_unit": "KG"
}
\`\`\`

**Résultat :**
- Catalogue affiche : 4,25€ / 500g
- Le restaurant peut commander par multiples de 500g
- Commande de 15 KG → 15 × 8.50 = 127,50€
- Fournisseur reçoit : 1,5 cartons

---

### CAS 3 : Pizza surgelée en carton (24 pièces)

**Contexte :** Pizza surgelée vendue en cartons de 24 pièces.

#### Produit NON DÉTAILLABLE

\`\`\`json
{
  "code": "PIZZA-MARG-001",
  "article": "Pizza Margherita surgelée",
  "supplier_id": "uuid-fournisseur",
  "supplier_packaging_quantity": 24,
  "supplier_packaging_unit": "U",
  "supplier_packaging_type": "carton",
  "supplier_package_price": 96.00,
  "is_divisible": false,
  "catalog_price": 96.00,
  "catalog_unit": "carton"
}
\`\`\`

**Résultat :**
- Catalogue affiche : 96,00€ / carton
- Le restaurant doit commander 1, 2, 3... cartons (pas de demi-carton)
- Système valide : Quantité = multiple du conditionnement

---

## 🔌 APIs DISPONIBLES

### Liste des endpoints

\`\`\`
GET    /api/products
  - Lister tous les produits du restaurant
  - Autorisation : User (connecté)
  - Filtres query params :
    • ?supplier_id={uuid} : Filtrer par fournisseur
    • ?is_available=true : Produits disponibles uniquement
  - Retour : Array[ProductResponse]

GET    /api/products/{product_id}
  - Détails d'un produit
  - Autorisation : User
  - Retour : ProductResponse

POST   /api/products
  - Créer un produit
  - Autorisation : Admin
  - Body : ProductCreate (JSON)
  - Retour : ProductResponse

PUT    /api/products/{product_id}
  - Modifier un produit
  - Autorisation : Admin
  - Body : ProductCreate (JSON)
  - Retour : ProductResponse

DELETE /api/products/{product_id}
  - Supprimer un produit
  - Autorisation : Admin
  - Retour : 200 OK

DELETE /api/products
  - Supprimer TOUS les produits du restaurant
  - Autorisation : Admin
  - Retour : { "message": "X produits supprimés" }

POST   /api/products/import
  - Import batch CSV/Excel
  - Autorisation : Admin
  - Body : ProductImportRequest (JSON)
  - Retour : ProductImportResponse

GET    /api/rayons
  - Liste des rayons prédéfinis
  - Autorisation : User
  - Retour : Array[{ id, nom, categorie }]

GET    /api/types
  - Liste des types prédéfinis
  - Autorisation : User
  - Retour : Array[{ id, nom, description }]
\`\`\`

---

### Payload minimum (POST /api/products)

\`\`\`json
{
  "code": "TEST-001",
  "article": "Produit Test",
  "supplier_id": "uuid-du-fournisseur"
}
\`\`\`

### Payload complet (nouveau système)

\`\`\`json
{
  "code": "TEST-001",
  "article": "Produit Test",
  "supplier_id": "uuid-du-fournisseur",
  "product_type": "frais",
  "rayon": "viandes-volailles",
  "supplier_packaging_quantity": 10,
  "supplier_packaging_unit": "KG",
  "supplier_packaging_type": "carton",
  "supplier_package_price": 85.00,
  "is_divisible": true,
  "divisible_quantity": 1,
  "divisible_unit": "KG",
  "catalog_price": 8.50,
  "catalog_unit": "KG",
  "is_available": true
}
\`\`\`

---

## 📥 IMPORT DE PRODUITS (CSV/Excel)

### Format CSV/Excel minimum

\`\`\`csv
Code,Article
REF001,Tomates cerises bio
REF002,Huile d'olive
\`\`\`

### Format CSV/Excel complet (nouveau système)

\`\`\`csv
Code,Article,Type,Rayon,Qté conditionnement,Unité conditionnement,Type conditionnement,Prix conditionnement (€),Détaillable (oui/non),Qté divisible,Unité divisible
REF001,Tomates cerises bio,frais,fruits-legumes-frais,5,KG,carton,12.50,oui,1,KG
REF002,Huile d'olive,epicerie-salee,huiles-vinaigres,6,L,colis,48.00,oui,1,L
REF003,Pizza Margherita,surgeles,surgeles,24,U,carton,96.00,non,,,
\`\`\`

### Processus d'import

1. **Upload fichier** (.xlsx, .xls, .csv)
2. **Parsing automatique** avec détection des en-têtes
3. **Mapping colonnes** : Association automatique + ajustement manuel
4. **Prévisualisation éditable** : Tableau où chaque ligne peut être modifiée
5. **Import** : Création batch avec gestion des doublons
6. **Résultat** : Nombre créés/mis à jour/erreurs

### API d'import

\`\`\`
POST /api/products/import

Body:
{
  "supplier_id": "uuid-du-fournisseur",
  "products": [
    {
      "code": "REF001",
      "article": "Produit 1",
      "supplier_packaging_quantity": 6,
      "supplier_packaging_unit": "L",
      "supplier_packaging_type": "colis",
      "supplier_package_price": 42.50,
      "is_divisible": true,
      "divisible_quantity": 1,
      "divisible_unit": "L"
    }
  ],
  "update_existing": false
}

Response:
{
  "created": 10,
  "updated": 2,
  "skipped": 0,
  "errors": ["Ligne 5: Prix invalide"]
}
\`\`\`

### Gestion des doublons

- **update_existing = false** : Produit ignoré (skipped)
- **update_existing = true** : Produit mis à jour

---

## 🏗️ ARCHITECTURE TECHNIQUE

### Backend
- **Framework** : FastAPI (Python)
- **Base de données** : MongoDB
- **Validation** : Pydantic models
- **Collections** :
  - \`products\` : Stockage des produits
  - \`suppliers\` : Stockage des fournisseurs
  - \`orders\` : Utilise les produits

### Frontend
- **Framework** : React
- **UI Library** : Shadcn UI + Tailwind CSS
- **Routing** : React Router
- **Gestion d'état** : Context API

### Import
- **Parsing** : SheetJS (xlsx) pour CSV/Excel
- **Validation** : Côté backend (Pydantic) + côté frontend (React)

---

## 🚫 LIMITES ACTUELLES

- ❌ Pas de gestion multi-tarifs (un seul prix par produit)
- ❌ Pas d'historique des prix
- ❌ Pas de gestion de stocks
- ❌ Pas de suggestions de produits similaires
- ❌ Pas de système de favoris individuels par utilisateur

---

## ✅ ÉVOLUTIONS POSSIBLES

- ✅ Import depuis API fournisseur
- ✅ Import depuis scan de facture PDF (via IA)
- ✅ Gestion de l'historique des prix
- ✅ Alertes de prix (variation importante)
- ✅ Système de favoris / paniers types
- ✅ Suggestions basées sur les commandes précédentes
- ✅ Export de catalogue (PDF, Excel)
- ✅ Duplication de produits
- ✅ Import/export entre restaurants

---

## 📌 POINTS CLÉS POUR IA

### Résumé pour compréhension rapide :

1. **Deux systèmes coexistent** : Nouveau (conditionnement) et Ancien (compatibilité)
2. **Champs minimum** : code + article + supplier_id
3. **Nouveau système** : Modélise précisément conditionnement ET détaillabilité
4. **Prix catalogue** : Calculé automatiquement si nouveau système utilisé
5. **Import flexible** : CSV/Excel avec mapping + prévisualisation éditable
6. **APIs complètes** : CRUD + Import batch + Filtres

### Vocabulaire clé :

- **Conditionnement** : Comment le fournisseur vend (colis de 6L, carton de 10kg)
- **Détaillable** : Possibilité de commander moins qu'un conditionnement complet
- **Prix catalogue** : Prix affiché au restaurant (auto-calculé depuis conditionnement)
- **Ancien système** : Champs texte libres pour compatibilité
- **Nouveau système** : Champs structurés pour précision

---

## 🎯 USAGE RECOMMANDÉ

Pour créer un nouveau produit dans OrderLab :

1. **Utiliser le NOUVEAU SYSTÈME** si possible
2. Remplir les champs de conditionnement fournisseur
3. Définir si le produit est détaillable
4. Laisser le prix catalogue se calculer automatiquement
5. Ajuster manuellement si nécessaire

Pour importer un catalogue fournisseur :

1. Demander le tarif au format Excel/CSV
2. Mapper les colonnes dans l'interface d'import
3. Éditer les lignes en prévisualisation pour enrichir
4. Importer en batch

---

FIN DE LA DOCUMENTATION
Version : 1.0
Date : 14 avril 2026
Projet : OrderLab - Système de gestion de commandes pour restaurants`}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="text-center py-6 text-sm text-gray-500">
          <p>Documentation générée le 14 avril 2026 • Version 1.0</p>
          <p className="mt-1">OrderLab - Système de gestion de commandes pour restaurants</p>
        </div>
      </div>
    </div>
  );
};

export default ProductDocumentation;
